import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import moment from "moment";
import { useForm, SubmitErrorHandler } from "react-hook-form";
import { Flex } from "@chakra-ui/react";
import Field from "ui/components/Input/Field";
import SelectUnidade from "ui/components/Input/SelectUnidade";
import { Row, Col } from "antd";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFCertidaoNegativaDebitos from "./PDFCertidaoNegativaDebitos";
import { FiltroCertidaoNegativaForm } from "core/features/relatorios/typings";
import { RelatorioCertidaoNegativaDebitosEvents } from "core/features/relatorios/enums/events";
import useRelatorioCertidaoNegativaDebitos from "core/features/relatorios/hooks/useRelatorioCertidaoNegativaDebitos";
import { showError } from "infra/helpers/alerta";
import { trackEvent } from "infra/helpers/event";
import ModalAssinaturasRelatorio from "components/Relatorio/ModalAssinaturas";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";

interface ExportCertidaoNegativaDebitosProps {
  defaultUnidadeId?: string;
  defaultDataInicio?: string;
  defaultDataFim?: string;
  showSubmitButton?: boolean;
}

const ExportCertidaoNegativaDebitos = forwardRef(
  (
    {
      defaultUnidadeId,
      defaultDataInicio,
      defaultDataFim = moment().format("YYYY-MM-DD"),
      showSubmitButton = true,
    }: ExportCertidaoNegativaDebitosProps,
    ref?: React.ForwardedRef<{
      submitForm: (callback?: () => any) => any;
    }>
  ) => {
    const callbackRef = useRef<Function | undefined>();
    const [modalAssinaturasOpen, setModalAssinaturasOpen] = useState(false);
    const { watch, control, handleSubmit, formState } =
      useForm<FiltroCertidaoNegativaForm>({
        mode: "all",
        defaultValues: {
          unidadeId: defaultUnidadeId,
          dataInicio: defaultDataInicio,
          dataFim: defaultDataFim,
        },
      });

    const _form = watch();

    const { mutate: getRelatorio, isLoading } =
      useRelatorioCertidaoNegativaDebitos();

    const { onDownload: _onExportPdf } = useDownloadRenderedPdf({
      fileName: `Certidão negativa de débitos - ${moment(_form.dataFim).format(
        "DD_MM_YYYY"
      )}`,
      Component: PDFCertidaoNegativaDebitos,
      loading: isLoading,
    });

    const curryOnSubmitAssinaturas = (assinaturas?: AssinaturasItem[]) => {
      return async (form: FiltroCertidaoNegativaForm) => {
        return new Promise((resolve, reject) => {
          trackEvent(RelatorioCertidaoNegativaDebitosEvents.Emitir);
          getRelatorio(form, {
            onSuccess: (response) => {
              _onExportPdf({
                data: response,
                assinaturas,
              }).then(resolve);
            },
            onError: reject,
          });
        });
      };
    };

    const onInvalid: SubmitErrorHandler<FiltroCertidaoNegativaForm> = async (
      e
    ) => {
      const error = e?.unidadeId || e?.dataInicio || e?.dataFim;

      const message = "Preencha os campos corretamente";

      return new Promise((resolve, reject) => {
        if (!error) {
          resolve(null);
        } else {
          showError(message);
          reject(message);
        }
      });
    };

    useImperativeHandle(ref, () => ({
      submitForm: (callback?: () => any) => {
        callbackRef.current = callback;
        setModalAssinaturasOpen(true);
      },
    }));

    return (
      <>
        <form>
          <Flex flexDir="column">
            <Flex mb={6}>
              <Field
                control={control}
                name="unidadeId"
                label={"Unidade"}
                rules={{
                  required: true,
                }}
                render={({ field, isInvalid }) => (
                  <SelectUnidade isInvalid={isInvalid} {...field} />
                )}
              />
            </Flex>

            <Row gutter={16}>
              <Col xs={12}>
                <Field
                  control={control}
                  name="dataInicio"
                  label="Data inicial do período"
                  inputProps={{
                    type: "date",
                  }}
                />
              </Col>

              <Col xs={12}>
                <Field
                  control={control}
                  name="dataFim"
                  label="Data final do período"
                  rules={{ required: true }}
                  inputProps={{
                    type: "date",
                  }}
                />
              </Col>
            </Row>

            {showSubmitButton ? (
              <PrimaryButton
                mt={4}
                type="button"
                onClick={() => setModalAssinaturasOpen(true)}
                marginLeft="auto"
                disabled={!formState.isValid}
              >
                Gerar certidão
              </PrimaryButton>
            ) : null}
          </Flex>
        </form>

        <ModalAssinaturasRelatorio
          isOpenModal={modalAssinaturasOpen}
          onClose={() => setModalAssinaturasOpen(false)}
          onSubmit={(assinaturas) => {
            const onSubmit = curryOnSubmitAssinaturas(assinaturas);
            handleSubmit(onSubmit, onInvalid)()
              .then(() => {
                callbackRef.current?.();
                callbackRef.current = undefined;
              })
              .catch(console.error);
          }}
        />
      </>
    );
  }
);

export default ExportCertidaoNegativaDebitos;
